declare const window: any;

import {
  EMBED_BRIDGE_SOURCE,
  EMBED_BRIDGE_VERSION,
  EmbedAdapterOptions,
  EmbedBridgeCommand,
  EmbedVideoAdapter,
  isEmbedBridgeMessage,
} from '../embed-video.model';

const READY_WATCHDOG_MS = 8000;

/**
 * Mounts `media-embed-bridge` in an iframe and talks to it over `postMessage` — the cross-platform
 * path (`embed-transport.policy.ts` picks it inside the native app). Same provider, same contract as
 * the in-page adapters: the component holding this cannot tell the difference.
 *
 * If `ready` never arrives within the watchdog window, `onFallback` is called so the component can
 * drop to a plain iframe (today's behaviour) instead of a blank box — mirrors the watchdogs
 * `scorm-comunication.service.ts` already runs for the SCORM engine.
 */
export class BridgeEmbedAdapter implements EmbedVideoAdapter {
  private iframe: HTMLIFrameElement | null = null;
  private readonly bridgeOrigin: string;
  private options: EmbedAdapterOptions | null = null;
  private lastKnownTime = 0;
  private lastKnownDuration = 0;
  private ready = false;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private readonly onMessage = (event: MessageEvent) => this.handleMessage(event);

  constructor(private readonly bridgeUrl: string, private readonly onFallback: () => void) {
    this.bridgeOrigin = new URL(bridgeUrl, window.location.href).origin;
  }

  mount(host: HTMLElement, options: EmbedAdapterOptions): void {
    this.options = options;

    const iframe = document.createElement('iframe');
    iframe.id = options.playerId;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    iframe.style.width = '100%';
    iframe.style.height = '100%';

    const params = new URLSearchParams({
      provider: options.provider,
      id: options.videoId,
      t: String(options.startAt || 0),
    });
    iframe.src = `${this.bridgeUrl}?${params.toString()}`;
    host.appendChild(iframe);
    this.iframe = iframe;

    window.addEventListener('message', this.onMessage);
    this.watchdog = setTimeout(() => {
      if (!this.ready) this.onFallback();
    }, READY_WATCHDOG_MS);
  }

  private handleMessage(event: MessageEvent): void {
    if (event.origin !== this.bridgeOrigin) return;
    if (!this.iframe || event.source !== this.iframe.contentWindow) return;
    if (!isEmbedBridgeMessage(event.data)) return;

    const message = event.data;
    if (typeof message.currentTime === 'number') this.lastKnownTime = message.currentTime;
    if (typeof message.duration === 'number') this.lastKnownDuration = message.duration;

    if (message.type === 'ready') {
      this.ready = true;
      if (this.watchdog) clearTimeout(this.watchdog);
    }

    switch (message.type) {
      case 'ready':
        this.options?.onEvent({ type: 'ready' });
        break;
      case 'playing':
        this.options?.onEvent({ type: 'playing' });
        break;
      case 'paused':
        this.options?.onEvent({ type: 'paused', currentTime: message.currentTime ?? 0 });
        break;
      case 'ended':
        this.options?.onEvent({ type: 'ended', currentTime: message.currentTime ?? 0 });
        break;
      case 'error':
        this.options?.onEvent({ type: 'error', error: message.error });
        break;
      case 'time':
        break;
    }
  }

  private postCommand(command: Omit<EmbedBridgeCommand, 'source' | 'v'>): void {
    this.iframe?.contentWindow?.postMessage(
      { source: EMBED_BRIDGE_SOURCE, v: EMBED_BRIDGE_VERSION, ...command },
      this.bridgeOrigin
    );
  }

  getCurrentTime(): number {
    return this.lastKnownTime;
  }

  getDuration(): number {
    return this.lastKnownDuration;
  }

  seekTo(seconds: number): void {
    this.postCommand({ type: 'seek', seconds });
  }

  play(): void {
    this.postCommand({ type: 'play' });
  }

  pause(): void {
    this.postCommand({ type: 'pause' });
  }

  destroy(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    window.removeEventListener('message', this.onMessage);
    this.iframe?.remove();
    this.iframe = null;
    this.options = null;
  }
}
